import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1643447727844-1e2e31544237";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-wrap">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.4, delay, ease: [0.19, 1, 0.22, 1] }}
    >{children}</motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 160]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.15]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="home" className="relative h-[100svh] w-full overflow-hidden bg-[#0B0B0B]" data-testid="hero-section">
      {/* BG image parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0B0B0B]" />
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.28] grayscale"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(11,11,11,0.9)_80%)]" />
      </motion.div>

      {/* Vertical hairline */}
      <div className="absolute left-6 md:left-12 top-24 bottom-24 hairline-v hidden md:block" />
      <div className="absolute right-6 md:right-12 top-24 bottom-24 hairline-v hidden md:block" />

      {/* Top meta */}
      <motion.div
        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 1 }}
        className="absolute top-24 md:top-28 left-0 right-0 flex justify-between px-6 md:px-24 text-[10px] font-cinzel tracking-[0.3em] text-white/50 z-10"
      >
        <span>EST · MUMBAI · KOLHAPUR</span>
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col justify-center px-6 md:px-24 max-w-[1600px] mx-auto">
        <MaskLine delay={0.3}>
          <span className="font-cinzel text-base md:text-lg tracking-[0.4em] text-[#C8A45D]">— NEXUS LEGAL</span>
        </MaskLine>

        <div className="mt-6 md:mt-8 font-serif-display leading-[1.02] text-white text-[10vw] md:text-[6.5vw] lg:text-[5.5vw]">
          <MaskLine delay={0.5}>Harshavardhan</MaskLine>
          <MaskLine delay={0.7}>Prakash Patil</MaskLine>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1.2 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div className="max-w-md">
            <div className="font-cinzel text-[10px] tracking-[0.35em] text-[#C8A45D] mb-3">ADVOCATE · BOMBAY HIGH COURT</div>
            <div className="text-white/60 text-sm mb-4 tracking-wider font-body">M.A., LL.M.</div>
            <div className="hairline w-16 mb-5" />
            <p className="font-serif-display text-2xl md:text-3xl text-white/90 italic leading-tight">Strategic Litigation.<br/>Practical Solutions.</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <button onClick={() => document.getElementById("contact")?.scrollIntoView({behavior:"smooth"})} className="btn-primary" data-testid="hero-book-btn">
              Book Consultation
            </button>
            <a href="https://wa.me/917666753201" target="_blank" rel="noreferrer" className="btn-primary" data-testid="hero-whatsapp-btn">
              <MessageCircle size={14}/> WhatsApp
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-cinzel text-[9px] tracking-[0.4em] text-white/40">SCROLL</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={14} className="text-[#C8A45D]" />
        </motion.div>
      </motion.div>
    </section>
  );
}
